// src/hooks/sync/usePrayerInboxImport.js
// Owner-side importer for the public prayer-request link.
//
// Listens to users/{uid}/prayerInbox (written by the public /pray/:token form),
// merges each submission into STORE_PRAYER_REQUESTS (same shape as a manual
// prayer) and, when the submitter shared a phone/Facebook link, creates or
// updates a contact in STORE_PEOPLE_CONTACTS. Inbox docs are deleted only after
// the prayer is confirmed present in the store and pending writes are flushed.
//
// Contact policy: blank fields are filled; a new phone number is APPENDED after
// existing numbers (max 5). The first (primary) number is never replaced.
import { useEffect, useRef, useState } from 'react';
import { collection, deleteDoc, limit, onSnapshot, query } from 'firebase/firestore';
import { toast } from 'sonner';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { useSyncStore, flushPendingSyncWrites } from '@/hooks/sync/useSyncStore';
import { STORE_PRAYER_REQUESTS, STORE_PEOPLE_CONTACTS, QUERY_LIMIT } from '@/lib/storeKeys';
import { PRAYER_CATEGORY_IDS, buildChecklistFromTopics } from '@/lib/prayerCategories';
import { formatDate } from '@/utils/helpers';

const CAT_SET = new Set(PRAYER_CATEGORY_IDS);

const str = (v, max) => (typeof v === 'string' ? v.trim().slice(0, max) : '');
const digits = (s) => String(s || '').replace(/\D/g, '');
const nameKey = (s) => String(s || '').toLowerCase().replace(/\s+/g, ' ').trim();
const phoneKey = (d) => (d && d.length >= 7 ? d.slice(-10) : '');

// 63917… / +63917… / 917… → 0917…  (People Keeper stores local 11-digit format)
const normalizePhone = (raw) => {
  let d = digits(raw);
  if (d.startsWith('63') && d.length === 12) d = '0' + d.slice(2);
  else if (d.length === 10 && d.startsWith('9')) d = '0' + d;
  return d.length >= 7 ? d : '';
};
// Same display format as PeopleKeeper's formatPhoneDisplay for 11 digits.
const formatPhone = (d) => (d.length === 11 ? `${d.slice(0, 4)}-${d.slice(4, 7)}-${d.slice(7)}` : d);

// Mirrors PeopleKeeper's normalizeNumbers, but returns [] when there are none.
const numbersOf = (c) => {
  if (Array.isArray(c?.contactNumbers) && c.contactNumbers.length) {
    return c.contactNumbers.filter((n) => String(n).trim());
  }
  if (c?.contactNumber && String(c.contactNumber).trim()) return [String(c.contactNumber).trim()];
  return [];
};

function normalizeSubmission(docId, d = {}) {
  const name     = str(d.submitterName, 80) || 'Someone';
  const given    = str(d.person, 120);
  const category = CAT_SET.has(d.category) ? d.category : 'personal';
  const topics   = d.topics && typeof d.topics === 'object' && !Array.isArray(d.topics) ? d.topics : {};
  const when     = typeof d.submittedAt?.toDate === 'function' ? d.submittedAt.toDate() : new Date();
  const iso      = when.toISOString();
  const checklist = buildChecklistFromTopics(category, topics);
  // Link-form submissions have no free-text request, so build a short one-line
  // summary from the ticked items to show on the prayer card.
  const typedText = str(d.text, 2000);
  const summary = typedText || checklist.filter((i) => i.checked).map((i) => i.label).join(', ').slice(0, 300);

  return {
    docId,
    whenMs:   when.getTime(),
    name,
    phone:    normalizePhone(str(d.contactPhone, 20)),
    facebook: str(d.contactFacebook, 200),
    prayer: {
      id:           `link_${docId}`,
      text:         summary,
      person:       given ? `${given} (via ${name})` : name,
      category,
      dateAdded:    formatDate(when),
      checklist,
      answered:     false,
      dateAnswered: null,
      praise:       '',
      createdAt:    iso,
      updatedAt:    iso,
      source:       'link',
      submittedBy:  name,
    },
  };
}

// Pure: returns { next, created, updated, changed } without touching the input.
function applyContactUpdates(list, subs) {
  let working = Array.isArray(list) ? list : [];
  const created = [];
  const updated = [];
  const now   = new Date().toISOString();
  const today = formatDate(new Date());

  for (const s of subs) {
    if (!s.phone && !s.facebook) continue; // nothing worth saving

    const key = phoneKey(s.phone);
    let idx = key
      ? working.findIndex((c) => numbersOf(c).some((n) => phoneKey(digits(n)) === key))
      : -1;
    if (idx === -1) idx = working.findIndex((c) => nameKey(c.name) === nameKey(s.name));

    if (idx === -1) {
      working = [{
        id:                   `link_c_${s.docId}`,
        date:                 today,
        name:                 s.name,
        age:                  '',
        birthday:             '',
        contactNumbers:       s.phone ? [formatPhone(s.phone)] : [],
        contactNumber:        s.phone || '',
        facebookUrl:          s.facebook || '',
        address:              '',
        generation:           '',
        connection:           'Prayer request link',
        topic:                'Sent a prayer request',
        nextStep:             '',
        contactFrequencyDays: 7,
        createdAt:            now,
        updatedAt:            now,
      }, ...working];
      created.push(s.name);
      continue;
    }

    const c = working[idx];
    const patch = {};
    const nums = numbersOf(c);

    if (key && !nums.some((n) => phoneKey(digits(n)) === key) && nums.length < 5) {
      patch.contactNumbers = [...nums, formatPhone(s.phone)];
      if (nums.length === 0) patch.contactNumber = s.phone; // first number only
    }
    if (s.facebook && !String(c.facebookUrl || '').trim()) {
      patch.facebookUrl = s.facebook;
    }

    if (Object.keys(patch).length > 0) {
      working = working.map((x, i) => (i === idx ? { ...x, ...patch, updatedAt: now } : x));
      updated.push(c.name || s.name);
    }
  }

  return { next: working, created, updated, changed: created.length + updated.length > 0 };
}

const listNames = (arr) => {
  const names = [...new Set(arr)];
  return names.slice(0, 3).join(', ') + (names.length > 3 ? ` +${names.length - 3} more` : '');
};

export function usePrayerInboxImport() {
  const { user } = useAuth();
  const uid = user?.uid && user.uid !== 'offline' ? user.uid : null;

  const [prayers,  setPrayers,  prayersReady]  = useSyncStore(STORE_PRAYER_REQUESTS, []);
  const [contacts, setContacts, contactsReady] = useSyncStore(STORE_PEOPLE_CONTACTS, []);

  const [inbox, setInbox] = useState([]); // [{ id, ref, data }]
  const [tick,  setTick]  = useState(0);

  const prayersRef  = useRef(prayers);
  const contactsRef = useRef(contacts);
  useEffect(() => { prayersRef.current = prayers; },   [prayers]);
  useEffect(() => { contactsRef.current = contacts; }, [contacts]);

  const handledRef  = useRef(new Set()); // inbox doc ids already merged this session
  const awaitingRef = useRef([]);        // [{ ref, prayerId }] waiting to be deleted
  const flushingRef = useRef(false);

  // ── 1. Listen to the inbox ────────────────────────────────────────────────
  useEffect(() => {
    if (!uid || !db) { setInbox([]); return undefined; }
    const q = query(collection(db, 'users', uid, 'prayerInbox'), limit(QUERY_LIMIT));
    const unsub = onSnapshot(
      q,
      (snap) => setInbox(snap.docs.map((d) => ({ id: d.id, ref: d.ref, data: d.data() }))),
      (err) => console.warn('[usePrayerInboxImport] inbox listener error:', err?.code || err),
    );
    return unsub;
  }, [uid]);

  // ── 2. Merge new submissions (only once both stores have loaded) ──────────
  useEffect(() => {
    if (!prayersReady || !contactsReady || inbox.length === 0) return;
    const fresh = inbox.filter((d) => !handledRef.current.has(d.id));
    if (fresh.length === 0) return;
    fresh.forEach((d) => handledRef.current.add(d.id));

    const subs = fresh
      .map((d) => normalizeSubmission(d.id, d.data))
      .sort((a, b) => a.whenMs - b.whenMs); // oldest first

    // Prayers — skip ids already present (e.g. a previous delete failed).
    const have = new Set((prayersRef.current || []).map((p) => p.id));
    const newPrayers = subs.map((s) => s.prayer).filter((p) => !have.has(p.id));
    if (newPrayers.length > 0) {
      setPrayers((prev) => {
        const list = Array.isArray(prev) ? prev : [];
        const ids  = new Set(list.map((p) => p.id));
        const add  = newPrayers.filter((p) => !ids.has(p.id)).reverse(); // newest first
        return add.length ? [...add, ...list] : list;
      });
    }

    // Contacts
    const outcome = applyContactUpdates(contactsRef.current, subs);
    if (outcome.changed) {
      setContacts((prev) => applyContactUpdates(prev, subs).next);
    }

    // Queue the inbox docs for deletion once the prayers are confirmed in state.
    awaitingRef.current.push(...fresh.map((d) => ({ ref: d.ref, prayerId: `link_${d.id}` })));
    setTick((t) => t + 1);

    if (newPrayers.length > 0) {
      const bits = [`From ${listNames(subs.map((s) => s.name))}`];
      if (outcome.created.length) bits.push(`Added to People: ${listNames(outcome.created)}`);
      if (outcome.updated.length) bits.push(`Updated contact: ${listNames(outcome.updated)}`);
      toast.success(
        `${newPrayers.length} new prayer request${newPrayers.length > 1 ? 's' : ''} received`,
        { description: bits.join(' · '), duration: 6000 },
      );
    }
  }, [inbox, prayersReady, contactsReady, setPrayers, setContacts]);

  // ── 3. Delete inbox docs after the prayer is in state and writes flushed ──
  useEffect(() => {
    if (awaitingRef.current.length === 0 || flushingRef.current) return;
    const have  = new Set((prayers || []).map((p) => p.id));
    const ready = awaitingRef.current.filter((a) => have.has(a.prayerId));
    if (ready.length === 0) return;

    flushingRef.current = true;
    awaitingRef.current = awaitingRef.current.filter((a) => !have.has(a.prayerId));

    (async () => {
      try {
        await new Promise((r) => setTimeout(r, 50)); // let sibling setState updaters run
        await flushPendingSyncWrites();
        await Promise.all(
          ready.map((a) =>
            deleteDoc(a.ref).catch((err) =>
              console.warn('[usePrayerInboxImport] could not delete inbox doc:', err?.code || err),
            ),
          ),
        );
      } finally {
        flushingRef.current = false;
        setTick((t) => t + 1); // re-check anything that arrived meanwhile
      }
    })();
  }, [prayers, tick]);
}

export default usePrayerInboxImport;